import { useSession } from "next-auth/client";
import { useContext, useEffect, useMemo, useState } from "react";
import { MultipleQueriesResponse } from "@algolia/client-search";
import Algolia from "lib/algolia";
import { DeprecatedAlgoliaSearchProduct } from "src/types/Algolia.d";
import {
  AlgoliaCarouselFilters,
  AlgoliaCarouselsHookProps,
  CarouselType,
} from "src/types/AlgoliaProductsCarousel.d";
import { AppCtx } from "src/contexts/app.context";
import { useAsync } from "src/hooks/useAsync";
import {
  BRAND_SLUG_FACET,
  GENDERS_FACET,
  IS_DELETED,
  LABELS_TYPE_FACET,
} from "src/utils/algolia";
import { useSegment } from "./useSegment";

interface SearchResults {
  index?: string;
  results?: DeprecatedAlgoliaSearchProduct[];
}

interface AlgoliaSearchData {
  products: SearchResults | null;
  loading: boolean;
}

const defaultHitsPerPage = 13;

const getAlgoliaFilters = (props: AlgoliaCarouselFilters): string => {
  const defaultFilters = [`${IS_DELETED}:false`];
  if (props.gender) {
    defaultFilters.push(`${GENDERS_FACET}:${props.gender}`);
  }
  if (
    props.carouselType === CarouselType.ICONS ||
    props.carouselType === CarouselType.POPULAR_PRODUCTS
  ) {
    defaultFilters.push(`${BRAND_SLUG_FACET}: ${props.brandSlug}`);
  }

  if (props.carouselType === CarouselType.ICONS) {
    defaultFilters.push(`${LABELS_TYPE_FACET}:icons`);
  }

  const searchFilters = defaultFilters.join(" AND ");

  return `${searchFilters}`;
};

const useAlgoliaProductsCarousel = (
  props: AlgoliaCarouselsHookProps
): AlgoliaSearchData => {
  const [session] = useSession();
  const { locale, isGhostDomain } = useContext(AppCtx);
  const [products, setProducts] = useState<SearchResults | null>(null);
  const [loading, setLoading] = useState(false);
  const { user: htUser } = useSegment();

  const userToken = useMemo(
    () => ({
      userToken: session?.user.user_id || htUser?.anonymousId() || "",
    }),
    [session?.user, htUser]
  );
  const prefix = isGhostDomain ? "zz" : locale;

  useEffect(
    () => () => {
      void Algolia.clearCache();
    },
    []
  );

  const search = (): Promise<
    MultipleQueriesResponse<unknown> | null | undefined
  > => {
    setLoading(true);

    const filters = getAlgoliaFilters(props);
    return Algolia.search([
      {
        indexName: `${prefix}_products${props.sortBy || ""}`,
        query: "",
        params: {
          hitsPerPage: props.hitsPerPage || defaultHitsPerPage,
          filters,
          ruleContexts: [...(props.ruleContexts || []), "react_web_shop"],
          clickAnalytics: true,
          enablePersonalization: !!props.enablePersonalization,
          userToken: userToken.userToken || "",
        },
      },
    ]);
  };
  const onSuccess = (
    data: MultipleQueriesResponse<unknown> | undefined | null
  ) => {
    setLoading(false);
    if (data) {
      const { results } = data;

      setProducts({
        results: results[0].hits as DeprecatedAlgoliaSearchProduct[],
      });
    }
  };
  useAsync(search, onSuccess, 50);
  return {
    products,
    loading,
  };
};

export default useAlgoliaProductsCarousel;
